import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useCallback } from 'react';
import { TEST_TOKEN_HEADER, type SavedAnswerView, type TestAnswer, type TestSessionView } from '@nest-recruit/shared';
import { request, type ApiError } from '../../../lib/api-client';

/**
 * ASM-03's calls. The link's token goes in a header on every one, never in the API
 * address (ADR 0019). A candidate has no session, so nothing here touches the staff
 * app's caches.
 */

/** The page as the server sent it, and when it arrived — what the countdown corrects by. */
export type TestSession = TestSessionView & { readonly receivedAt: number };

function received(view: TestSessionView): TestSession {
  return { ...view, receivedAt: Date.now() };
}

export function testSessionKey(token: string): readonly unknown[] {
  return ['test-session', token];
}

function headers(token: string): Readonly<Record<string, string>> {
  return { [TEST_TOKEN_HEADER]: token };
}

export function useTestSession(token: string): UseQueryResult<TestSession, ApiError> {
  return useQuery({
    queryKey: testSessionKey(token),
    queryFn: async () => received((await request<TestSessionView>('/test-session', { headers: headers(token) })).data),
    enabled: token !== '',
    retry: (count, error) => error.status === 0 && count < 3,
    refetchOnWindowFocus: true,
  });
}

/** Start, next section and submit all answer with the whole page, which replaces the cache. */
export function useTestStep(token: string): (step: 'start' | 'next-section' | 'submit') => Promise<TestSession> {
  const queryClient = useQueryClient();
  return useCallback(
    async (step) => {
      const view = received((await request<TestSessionView>(`/test-session/${step}`, { method: 'POST', headers: headers(token) })).data);
      queryClient.setQueryData(testSessionKey(token), view);
      return view;
    },
    [queryClient, token],
  );
}

export async function saveTestAnswer(token: string, questionId: string, answer: TestAnswer): Promise<SavedAnswerView> {
  return (await request<SavedAnswerView>(`/test-session/answers/${questionId}`, { method: 'PUT', body: { answer }, headers: headers(token) })).data;
}

export async function uploadTestAnswer(token: string, questionId: string, file: File): Promise<SavedAnswerView> {
  const form = new FormData();
  form.append('file', file, file.name);
  return (await request<SavedAnswerView>(`/test-session/answers/${questionId}/file`, { method: 'POST', body: form, headers: headers(token) })).data;
}
